@use 'sass:map';

:host {
  --sizing-close: var(--pine-dimension-125);

  align-items: center;
  border-radius: var(--pine-dimension-sm);
  display: inline-flex;
  padding-block: var(--pine-dimension-025);
  padding-inline: var(--pine-dimension-100);
}

$pds-chip-sentiment: (
  accent: var(--pine-chip-color-accent),
  danger: var(--pine-chip-color-danger),
  info: var(--pine-chip-color-info),
  neutral: var(--pine-chip-color-neutral),
  success: var(--pine-chip-color-success),
  warning: var(--pine-chip-color-warning),
);

$pds-chip-sentiment-text: (
  accent: var(--pine-chip-color-accent-text),
  danger: var(--pine-chip-color-danger-text),
  info: var(--pine-chip-color-info-text),
  neutral: var(--pine-chip-color-neutral-text),
  success: var(--pine-chip-color-success-text),
  warning: var(--pine-chip-color-warning-text),
);

$pds-chip-sentiment-dots: (
  accent: var(--pine-chip-color-accent-dot),
  danger: var(--pine-chip-color-danger-dot),
  info: var(--pine-chip-color-info-dot),
  neutral: var(--pine-chip-color-neutral-dot),
  success: var(--pine-chip-color-success-dot),
  warning: var(--pine-chip-color-warning-dot),
);

$pds-chip-sentiment-hover: (
  accent: var(--pine-chip-color-accent-hover),
  danger: var(--pine-chip-color-danger-hover),
  info: var(--pine-chip-color-info-hover),
  neutral: var(--pine-chip-color-neutral-hover),
  success: var(--pine-chip-color-success-hover),
  warning: var(--pine-chip-color-warning-hover),
);

@each $sentiment, $value in $pds-chip-sentiment {
  :host(.pds-chip--#{$sentiment}) {
    background: $value;
    border: var(--pine-border-width) solid var(--pine-chip-color-#{$sentiment}-hover);

    // dot colors
    .pds-chip__dot {
      /* stylelint-disable-next-line */
      background: map.get($pds-chip-sentiment-dots, $sentiment);
    }

    .pds-chip__label, .pds-chip__button, .pds-chip__close {
      /* stylelint-disable-next-line */
      color: map.get($pds-chip-sentiment-text, $sentiment);
      font-weight: var(--pine-font-weight-medium);
    }

    // tag close hover colors
    .pds-chip__close:hover {
      /* stylelint-disable-next-line */
      background: map.get($pds-chip-sentiment-hover, $sentiment);
    }
  }
  // dropdown hover colors
  :host(.pds-chip--#{$sentiment}.pds-chip--dropdown:hover) {
    /* stylelint-disable-next-line */
    background: map.get($pds-chip-sentiment-hover, $sentiment);
  }
}

.pds-chip__dot {
  border: var(--pine-border-width) solid transparent;
  border-radius: var(--pine-border-radius-full);
  display: inline-block;
  height: var(--pine-dimension-2xs);
  margin-inline-end: var(--pine-dimension-2xs);
  width: var(--pine-dimension-2xs);
}

.pds-chip__label {
  align-items: center;
  display: flex;

  pds-icon {
    margin-inline-end: var(--pine-dimension-2xs);
  }
}

.pds-chip__label, .pds-chip__button {
  font: var(--pine-typography-body-medium);
  letter-spacing: var(--pine-letter-spacing);
}

// dropdown

:host(.pds-chip--dropdown) {
  padding: var(--pine-dimension-none);
}

.pds-chip__button {
  align-items: center;
  appearance: none;
  background: transparent;
  border: var(--pine-dimension-none);
  border-radius: var(--pine-dimension-sm);
  cursor: pointer;
  display: flex;
  padding: var(--pine-dimension-025) var(--pine-dimension-100);

  &:focus-visible {
    outline: var(--pine-outline-focus);
    outline-offset: var(--pine-border-width);
  }

  pds-icon:first-child {
    margin-inline-end: var(--pine-dimension-2xs);
  }

  pds-icon:last-child {
    margin-inline-end: calc(var(--pine-dimension-025) * -1);
    margin-inline-start: var(--pine-dimension-2xs);
  }
}

.pds-chip__close {
  appearance: none;
  background: transparent;
  border: 0;
  border-radius: var(--pine-border-radius-full);
  box-sizing: border-box;
  height: var(--sizing-close);
  margin-inline-end: calc(var(--pine-dimension-xs) * -1);
  margin-inline-start: var(--pine-dimension-2xs);
  padding: var(--sizing-close);
  position: relative;
  width: var(--sizing-close);

  pds-icon {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &:focus-visible {
    outline: var(--pine-outline-focus);
  }
}

// small

:host(.pds-chip--sm) {
  --sizing-close: var(--pine-dimension-100);
  padding-block: var(--pine-dimension-025);
  padding-inline: var(--pine-dimension-100);

  .pds-chip__label, .pds-chip__button {
    font: var(--pine-typography-body-sm-medium);
    letter-spacing: var(--pine-letter-spacing);
  }
}

:host(.pds-chip--sm.pds-chip--dropdown) {
  padding: var(--pine-dimension-none);

  .pds-chip__button {
    padding: var(--pine-dimension-025) var(--pine-dimension-100);
  }
}

:host(.pds-chip--sm.pds-chip--brand) {
  .pds-chip__label {
    padding: var(--pine-dimension-025) var(--pine-dimension-100);
  }
}

// large

:host(.pds-chip--lg) {
  padding-block: var(--pine-dimension-025);
  padding-inline: var(--pine-dimension-150);

  .pds-chip__label, .pds-chip__button {
    font-family: var(--pine-font-family-heading);
    font-size: var(--pine-font-size-heading-6);
    font-weight: var(--pine-font-weight-medium);
    letter-spacing: var(--pine-letter-spacing-heading-6);
  }
}

:host(.pds-chip--lg.pds-chip--dropdown) {
  padding: var(--pine-dimension-none);

  .pds-chip__button {
    padding: var(--pine-dimension-025) var(--pine-dimension-150);
  }
}

:host(.pds-chip--brand) {
  /* stylelint-disable-next-line pine-design-system/no-hardcoded-colors */
  background: linear-gradient(90deg, #FF3E14 0%, #6B62F2 100%);
  border: 0;
  padding: 1px;
  position: relative;

  .pds-chip__label {
    /* stylelint-disable-next-line pine-design-system/no-hardcoded-colors */
    background: rgba(255, 255, 255, 0.9);
    border-radius: calc(var(--pine-dimension-sm) - 1px);
    /* stylelint-disable-next-line pine-design-system/prefer-semantic-tokens */
    color: var(--pine-color-grey-900);
    font-weight: var(--pine-font-weight-medium);
    padding: var(--pine-dimension-025) var(--pine-dimension-100);
    position: relative;
    z-index: var(--pine-z-index-raised);

    pds-icon {
      margin-inline-end: var(--pine-dimension-2xs);
    }
  }

  .pds-chip__button, .pds-chip__close {
    /* stylelint-disable-next-line pine-design-system/prefer-semantic-tokens */
    color: var(--pine-color-grey-900);
    font-weight: var(--pine-font-weight-medium);
    position: relative;
    z-index: var(--pine-z-index-raised);
  }

  .pds-chip__button {
    pds-icon:first-child {
      margin-inline-end: var(--pine-dimension-2xs);
    }
  }

  // tag close hover colors
  .pds-chip__close:hover {
    /* stylelint-disable-next-line pine-design-system/no-hardcoded-colors */
    background: rgba(255, 255, 255, 0.8);
  }
}
